import type { ISourceTableEntry } from './types.js';

/**
 * Raw / curated tables that feed the canonical concept definitions.
 *
 * Originally sourced verbatim from the PRODOPSAI-410 epic's "Data Sources"
 * section (flow metrics). PRODOPSAI-441 added the SonarCloud / fabric tables
 * that drive the quality concepts. This list is a documentation reference
 * only — no schema is introduced by the calculation-reference module.
 */
export const SOURCE_TABLES: ReadonlyArray<ISourceTableEntry> = [
  {
    tableName: 'raw_fact_issue_in_progress_daily',
    purpose:
      'Fabric mirror, daily snapshot of in-progress issues. ~168 564 rows verified, complete daily series 2025-01-03 → 2026-05-10, ~750–800 in-progress issues per day. Never modified by Skystone.',
    notableColumns: [
      'snapshot_date',
      'issue_key',
      'issue_type',
      'in_progress_flag',
      'team_id',
      'product_id',
      'archived',
    ],
    feeds: ['wip-current', 'wip-trend', 'wip-avg-age', 'wip-aging'],
  },
  {
    tableName: 'raw_fact_issue_current',
    purpose:
      'Fabric mirror, one row per current issue. Used for `first_in_progress_date` (age source), `status_normalized` (Open filter), `is_done` / `last_done_date` (throughput).',
    notableColumns: [
      'issue_key',
      'first_in_progress_date',
      'status_normalized',
      'issue_status',
      'is_done',
      'last_done_date',
      'days_in_progress',
      'archived',
      'issue_type',
    ],
    feeds: ['wip-avg-age', 'wip-aging', 'open', 'total', 'cancelled', 'throughput'],
  },
  {
    tableName: 'raw_dim_issue',
    purpose: 'Issue dimension table. Used for `epic_key` joins on per-epic WIP.',
    notableColumns: ['issue_key', 'epic_key'],
    feeds: ['wip-current'],
  },
  {
    tableName: 'raw_dim_team',
    purpose: 'Team dimension. Used as a scope filter for per-team WIP / Open / Throughput and for the code-quality cohort joins.',
    notableColumns: ['team_id', 'team_name', 'product_id', 'archived'],
    feeds: [
      'wip-current',
      'wip-trend',
      'open',
      'throughput',
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-reliability-trend',
      'cq-maintainability-trend',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
    ],
  },
  {
    tableName: 'raw_dim_product',
    purpose: 'Product dimension. Used as a scope filter for per-product WIP / Open / Throughput and as the join hub for code-quality team / repository membership.',
    notableColumns: ['product_id', 'product_name', 'archived'],
    feeds: [
      'wip-current',
      'wip-trend',
      'wip-aging',
      'open',
      'throughput',
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-reliability-trend',
      'cq-maintainability-trend',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
    ],
  },
  {
    tableName: 'jira_issues',
    purpose:
      'Jira-direct mirror; one row per issue; populated by IssueSyncService. Source of current status and the anchor for changelog joins. Independent of Fabric.',
    notableColumns: [
      'id',
      'issue_key',
      'project_id',
      'issue_type',
      'current_status',
      'last_updated',
    ],
    feeds: ['status-count'],
  },
  {
    tableName: 'jira_status_changes',
    purpose:
      'Jira-direct changelog; one row per status transition; populated by IssueSyncService. Used to reconstruct per-issue status segments for window-overlap counting.',
    notableColumns: ['id', 'issue_id', 'from_status', 'to_status', 'changed_at'],
    feeds: ['status-count'],
  },
  {
    tableName: 'raw_fact_sonarqube_project_quality_latest',
    purpose:
      'Fabric mirror, one row per SonarCloud project containing the latest analysis result. Snapshot table — used by every "current state" code-quality calculation (verdict-strip tiles, distribution panels, freshness, team breakouts).',
    notableColumns: [
      'project_key',
      'quality_status',
      'coverage_pct',
      'reliability_grade',
      'maintainability_grade',
      'last_analysis_at',
      'ingestion_date',
    ],
    feeds: [
      'cq-coverage-current',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
    ],
  },
  {
    tableName: 'raw_fact_sonarqube_project_quality_history',
    purpose:
      'Fabric mirror, time series of SonarCloud metric readings. One row per (project, metric_key, metric_at). Used by every trend/sparkline calculation on the Code Quality tab.',
    notableColumns: [
      'project_key',
      'metric_key',
      'metric_value',
      'metric_at',
    ],
    feeds: ['cq-coverage-trend', 'cq-reliability-trend', 'cq-maintainability-trend'],
  },
  {
    tableName: 'raw_dim_sonarqube_project',
    purpose: 'SonarCloud project dimension. Joins a SonarCloud `project_key` to a Skystone repository (`repository_id`). Critical for cohort definition — repos without a row here are invisible to every code-quality calculation.',
    notableColumns: ['project_key', 'repository_id', 'organization'],
    feeds: [
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-reliability-trend',
      'cq-maintainability-trend',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
    ],
  },
  {
    tableName: 'raw_dim_repository',
    purpose: 'Repository dimension. Used by code-quality cohort joins (`r.archived = false` filter) and by the snapshot dedupe (one project per repository wins via DISTINCT ON r.id).',
    notableColumns: ['id', 'repository_name', 'archived'],
    feeds: [
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-reliability-trend',
      'cq-maintainability-trend',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
    ],
  },
  {
    tableName: 'raw_bridge_repository_product',
    purpose: 'Many-to-many bridge between repositories and products. Used by every code-quality concept to scope-filter the cohort by product / team.',
    notableColumns: ['repository_id', 'product_id'],
    feeds: [
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-reliability-trend',
      'cq-maintainability-trend',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
    ],
  },
  {
    tableName: 'fabric_sync_run',
    purpose: 'Skystone-owned sync-run audit log. One row per ingestion job execution with counters for matched / unmatched / ambiguous entities. The code-quality caveat banner reads the most recent successful FABRIC_SONARQUBE_SYNC row.',
    notableColumns: ['id', 'job_type', 'entity_type', 'status', 'counters', 'started_at', 'completed_at'],
    feeds: ['cq-sync-caveats'],
  },
] as const;
